import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useUserOrNull } from "shared-ts-utils-authentication";
import { useAppInfoOrNull } from "shared-ts-app-info";
import { NonNullableSettingConfig } from "./NonNullableSettingConfig";
import { UserSettingsContext } from "./UserSettingsContext";
import { SettingKey } from "./SettingKeyDto";
import { NonAsyncSettingsStorage } from "./storage/NonAsyncSettingsStorage";
import { AsyncSettingsStorage } from "./storage/AsyncSettingsStorage";
import {
  IllegalStateError,
  ObjectUtils,
  UnsupportedError,
} from "shared-ts-utils-core";
import { NonNullableSettingOverrides } from "./NonNullableSettingOverrides";
import { SettingConfig } from "./SettingConfig";
import { SettingOverrides } from "./SettingOverrides";
import { SettingsStorage } from "./storage/SettingsStorage";
import { useMemoizeNoNullableSettingConfig } from "./useMemoizeSettingConfig";

export type UseUserSettingReturnType<TValue> = [
  TValue,
  Dispatch<SetStateAction<TValue>>
];
export const useUserSetting = <TValue>(
  name: string,
  settingConfig: NonNullableSettingConfig<TValue>,
  overrides?: NonNullableSettingOverrides<TValue>
): UseUserSettingReturnType<TValue> => {
  const memoizedSettingConfig =
    useMemoizeNoNullableSettingConfig<TValue>(settingConfig);
  const appInfo = useAppInfoOrNull();
  const user = useUserOrNull();
  const { settings, setSettings, storage, localStorage } =
    useContext(UserSettingsContext);

  const settingKey = useMemo(
    () => SettingKey.from(name, appInfo?.key, user?.id ?? null),
    [name, appInfo?.key, user?.id]
  );

  let valueAsString = settings[settingKey.asString] ?? null;

  if (overrides?.storeLocally === true) {
    valueAsString = localStorage.readRawValue(
      settingKey,
      memoizedSettingConfig as SettingConfig<TValue>,
      overrides as SettingOverrides<TValue>
    );

    if (valueAsString == null && user?.id != null) {
      // try to read from app default
      const appDefaultKey = SettingKey.from(name, appInfo?.key, appInfo?.key);
      valueAsString = localStorage.readRawValue(
        appDefaultKey,
        memoizedSettingConfig as SettingConfig<TValue>,
        overrides as SettingOverrides<TValue>
      );
    }
  }

  if (valueAsString == null && user?.id != null) {
    // try to read from app default
    const appDefaultKey = SettingKey.from(name, appInfo?.key, appInfo?.key);
    valueAsString = settings[appDefaultKey.asString] ?? null;
  }

  const value = useDeserializeValue(
    settingKey,
    valueAsString,
    overrides,
    memoizedSettingConfig
  );

  const setValue_: Dispatch<SetStateAction<TValue>> = useCallback(
    (newValueOrFn: SetStateAction<TValue>) => {
      const prevValueSerialized = valueAsString;
      const newValue = resolveNewValue<TValue>(value, newValueOrFn);

      const serializeValue = (val: TValue | null): string | null => {
        if (val == null) return null;
        return overrides?.serialize != null
          ? overrides.serialize(val)
          : memoizedSettingConfig.serialize(val);
      };

      const newValueSerialized = serializeValue(newValue);
      const isChange = !ObjectUtils.equals(
        prevValueSerialized,
        newValueSerialized
      );

      if (isChange) {
        setSettings((prev) => ({
          ...prev,
          [settingKey.asString]: newValueSerialized,
        }));

        writeToStorage(
          settingKey,
          newValue,
          memoizedSettingConfig,
          overrides,
          storage,
          localStorage
        );

        if (overrides?.storeCopyToAppLocally === true) {
          const appSettingKey = SettingKey.from(name, appInfo?.key, null);
          localStorage.writeValue(
            appSettingKey,
            newValue,
            memoizedSettingConfig as SettingConfig<TValue>,
            overrides as SettingOverrides<TValue>
          );
        }
      }
    },
    [
      valueAsString,
      value,
      overrides,
      memoizedSettingConfig,
      setSettings,
      storage,
      settingKey,
      localStorage,
      name,
      appInfo?.key,
    ]
  );

  return [value, setValue_];
};

const useDeserializeValue = <TValue>(
  settingKey: SettingKey,
  valueAsString: string | null,
  overrides: NonNullableSettingOverrides<TValue> | undefined,
  settingConfig: NonNullableSettingConfig<TValue>
): TValue => {
  return useMemo(() => {
    if (valueAsString == null) {
      throw new IllegalStateError(
        ">" + settingKey.asString + " is unexpectedly null"
      );
    }

    if (overrides?.deserialize != null) {
      return overrides.deserialize(valueAsString);
    }

    return settingConfig.deserialize(valueAsString);
  }, [valueAsString, overrides, settingConfig, settingKey.asString]);
};

const resolveNewValue = <TValue>(
  prevValue: TValue,
  newValueOrFn: SetStateAction<TValue>
): TValue => {
  let newValue: TValue;
  if (typeof newValueOrFn === "function") {
    const fn: (prevState: TValue) => TValue = newValueOrFn as any;
    newValue = fn(prevValue);
  } else {
    newValue = newValueOrFn;
  }
  return newValue;
};

const writeToStorage = <TValue>(
  key: SettingKey,
  value: TValue,
  settingConfig: NonNullableSettingConfig<TValue>,
  overrides: NonNullableSettingOverrides<TValue> | undefined,
  storage: SettingsStorage,
  localStorage: NonAsyncSettingsStorage
): void => {
  if (overrides?.storeLocally === true) {
    writeToStorage2(key, value, settingConfig, overrides, localStorage);
  } else {
    writeToStorage2(key, value, settingConfig, overrides, storage);
  }
};

const writeToStorage2 = <TValue>(
  key: SettingKey,
  value: TValue,
  settingConfig: NonNullableSettingConfig<TValue>,
  overrides: NonNullableSettingOverrides<TValue> | undefined,
  storage: SettingsStorage
): void => {
  const configAsSettingConfig = settingConfig as SettingConfig<TValue>;
  const overridesAsSettingOverrides = overrides as
    | SettingOverrides<TValue>
    | undefined;

  if (storage.type === "NonAsync") {
    (storage as NonAsyncSettingsStorage).writeValue(
      key,
      value,
      configAsSettingConfig,
      overridesAsSettingOverrides
    );
  } else if (storage.type === "Async") {
    (storage as AsyncSettingsStorage).writeValue(
      key,
      value,
      configAsSettingConfig,
      overridesAsSettingOverrides
    );
  } else {
    throw new UnsupportedError("Unsupported SettingsStorage: " + storage.type);
  }
};
